import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/lib/i18n";
import Logo from "./Logo";

export const scrollToHash = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Nav() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: t.nav.oasi, hash: "#oasi" },
    { label: t.nav.comfort, hash: "#comfort" },
    { label: t.nav.recensioni, hash: "#recensioni" },
    { label: t.nav.galleria, hash: "#galleria" },
    { label: t.nav.posizione, hash: "#posizione" },
  ];

  const go = (hash) => (e) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => scrollToHash(hash), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-0 z-40 transition-colors duration-500 ${
          scrolled ? "bg-sand/75 backdrop-blur-xl border-b border-line/70" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-10">
          <a
            href="#oasi"
            onClick={go("#oasi")}
            data-testid="nav-logo"
            className="hover:opacity-80 transition-opacity duration-300"
          >
            <Logo dark={!scrolled} />
          </a>

          <nav className="hidden items-center gap-8 lg:flex">
            {links.map((l) => (
              <a
                key={l.hash}
                href={l.hash}
                onClick={go(l.hash)}
                className={`group relative text-[13px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  scrolled ? "text-ink/80 hover:text-ink" : "text-sand hover:text-white"
                }`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-terra transition-all duration-400 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-5">
            <div
              className={`flex items-center rounded-full border p-0.5 text-[11px] font-semibold tracking-widest transition-colors duration-500 ${
                scrolled ? "border-ink/15" : "border-sand/50"
              }`}
              data-testid="lang-toggle"
            >
              {["it", "en"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`rounded-full px-2.5 py-1 uppercase transition-all duration-300 ${
                    lang === l ? "bg-ink text-sand" : scrolled ? "text-ink/60 hover:text-ink" : "text-sand/90 hover:text-white"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>

            <a
              href="#contatti"
              onClick={go("#contatti")}
              data-testid="nav-cta"
              className="hidden rounded-full bg-terra px-5 py-2.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-sand transition-all duration-300 hover:bg-ink hover:shadow-lg md:inline-block"
            >
              {t.nav.cta}
            </a>

            <button
              data-testid="nav-menu-btn"
              onClick={() => setOpen(true)}
              className={`grid h-10 w-10 place-items-center rounded-full border transition-colors duration-500 lg:hidden ${
                scrolled ? "border-ink/15 text-ink" : "border-sand/50 text-sand"
              }`}
              aria-label="Menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex flex-col bg-ink text-sand"
          >
            <div className="flex h-[72px] items-center justify-between px-5">
              <Logo dark />
              <button
                data-testid="nav-close-btn"
                onClick={() => setOpen(false)}
                className="grid h-10 w-10 place-items-center rounded-full border border-sand/25"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col items-start justify-center gap-2 px-8">
              {[...links, { label: t.nav.cta, hash: "#contatti" }].map((l, i) => (
                <motion.a
                  key={l.hash}
                  href={l.hash}
                  onClick={go(l.hash)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="font-serif text-4xl font-light italic text-sand/90 hover:text-terra transition-colors duration-300"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
